<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="500px"
  >
    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="Employee Disciplines"
      text="Edit Employee Disciplines"
      class="mb-0"
      :loading="loading"
    >
      <v-container v-if="dialog && !loading">
        <v-row no-gutters>
          <v-col cols="12">
            <v-autocomplete
              v-model="disciplinaryCategory"
              v-validate="'required'"
              :error-messages="errors.collect('disciplinaryCategory')"
              :items="disciplinaryCategories"
              outlined
              data-vv-name="disciplinaryCategory"
              data-vv-as="disciplinary category"
              item-text="name"
              item-value="id"
              label="Disciplinary Category"
            />
          </v-col>
          
          <v-col cols="12">
            <v-autocomplete
              v-model="typeOfMisconduct"
              v-validate="'required'"
              :error-messages="errors.collect('typeOfMisconduct')"
              :items="typeOfMisconducts"
              outlined
              data-vv-name="typeOfMisconduct"
              data-vv-as="type of misconduct"
              :item-text="item => '['+item.code+'] - '+ item.name"
              item-value="id"
              label="Type of Misconduct"
            >
              <template v-slot:selection="data">
                <span v-html="'['+data.item.code+'] - ' + data.item.name" />
              </template>
              
              <template v-slot:item="data">
                <v-list-item-content style="max-width:400px">
                  <p
                    class="font-weight-bold"
                    v-html="'['+ data.item.code +'] '+ data.item.name"
                  />
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12">
            <v-menu
              v-model="dateOfMisconductMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="dateOfMisconductDisplay"
                  v-validate="'required'"
                  label="Date of Misconduct"
                  outlined
                  :error-messages="errors.collect('dateOfMisconductDisplay')"
                  data-vv-name="dateOfMisconductDisplay"  
                  data-vv-as="date of misconduct"      
                  readonly
                  class="fix-date-picker-outline-overlap"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="dateOfMisconduct"
                @input="dateOfMisconductMenu = false"
              />
            </v-menu>
          </v-col>

          <v-col cols="12">
            <v-autocomplete
              v-model="disciplinaryAction"
              v-validate="'required'"
              :error-messages="errors.collect('disciplinaryAction')"
              :items="disciplinaryActions"
              data-vv-name="disciplinaryAction"
              data-vv-as="disciplinary action"
              item-text="name"
              item-value="id"
              outlined
              label="Disciplinary Action"
            />
          </v-col>

          <v-col cols="12">
            <v-menu
              v-model="dateOfActionTakenMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="dateOfActionTakenDisplay"
                  v-validate="'required'"
                  outlined
                  label="Date of Action Taken"
                  :error-messages="errors.collect('dateOfActionTakenDisplay')"
                  data-vv-name="dateOfActionTakenDisplay"  
                  data-vv-as="date of action taken"      
                  readonly
                  class="fix-date-picker-outline-overlap"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="dateOfActionTaken"
              />
            </v-menu>
          </v-col>
          
          <v-col cols="12">
            <v-textarea
              v-model="remark"
              outlined
              :rows="4"
              label="Remark"
            />
          </v-col>
        </v-row>
      </v-container>

      <v-card-actions v-if="!loading">
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          class="px-10"
          @click="dialog = false"
        >
          Close
        </v-btn>
        <v-btn
          :loading="progressing"
          color="hrissecondary"    
          class="white--text px-10"   
          @click="submitForm"
        >
          Update
        </v-btn>
      </v-card-actions>
    </material-card>
  </v-dialog>
</template>

<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      employeeUid: {
        type: String, 
        required: true,
      },
    },
    data () {
      return {
        progressing: false,
        loading: false,
        dialog: false,
        disciplinaryActions: [],
        disciplinaryCategories: [],
        typeOfMisconducts: [],
        typeOfMisconduct: null,
        disciplinaryAction: null,
        disciplinaryCategory: null,
        remark: null,
        dateOfActionTakenMenu: false,
        dateOfActionTaken: null,
        dateOfMisconductMenu: false,
        dateOfMisconduct: null,
        disciplinaryCaseId: null,
      }
    },

    computed: {
      dateOfActionTakenDisplay () {
        return this.dateOfActionTaken ? this.$moment(this.dateOfActionTaken, 'YYYY-MM-DD').format('D MMM YYYY') : ''
      },
      dateOfMisconductDisplay () {
        return this.dateOfMisconduct ? this.$moment(this.dateOfMisconduct, 'YYYY-MM-DD').format('D MMM YYYY') : ''
      },
    },

    watch: {
      dialog (value) {
        if (!value) return this.$resetData()

        this.loading = true
        Promise.all([
          this.$api.disciplinaryActions().list(),
          this.$api.disciplinaryCategories().list(),
          this.$api.typeOfMisconducts().list(),
        ])
          .then(([disciplinaryAction, disciplinaryCategory, typeOfMisconduct]) => {
            this.disciplinaryActions = disciplinaryAction.data
            this.disciplinaryCategories = disciplinaryCategory.data
            this.typeOfMisconducts = typeOfMisconduct.data

            this.loading = false
          })
      },
    },
    methods: {
      toggleDialog (disciplinaryCase) {
        this.disciplinaryCategory = disciplinaryCase.disciplinary_category_id
        this.typeOfMisconduct = disciplinaryCase.type_of_misconduct_id
        this.disciplinaryAction = disciplinaryCase.disciplinary_action_id
        this.remark = (disciplinaryCase.remarks === 'null' ? '' : disciplinaryCase.remarks)
        this.dateOfActionTaken = disciplinaryCase.date_of_action_taken
        this.dateOfMisconduct = disciplinaryCase.date_of_misconduct

        this.disciplinaryCaseId = disciplinaryCase.id

        this.dialog = true
      },
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.progressing = true

            this.$api.employeeDisciplineCases().update(this.disciplinaryCaseId, this.prepareFormData())
              .then(response => {
                if (response.status >= 200 && response.status < 300) {
                  this.$emit('updated')
                  this.$snack({ message: response.data.status })

                  this.dialog = false
                }
              })
              .catch(() => {})
              .then(() => {
                this.progressing = false
              })
          })
      },
      prepareFormData () {
        return {
          disciplinary_category_id: this.disciplinaryCategory,
          type_of_misconduct_id: this.typeOfMisconduct,
          disciplinary_action_id: this.disciplinaryAction,
          remarks: this.remark,
          date_of_action_taken: this.dateOfActionTaken,
          date_of_misconduct: this.dateOfMisconduct,
        }
      },
    },
  }
</script>

<style scoped>
.fix-date-picker-outline-overlap >>> label{
  background: white;
  padding-right:10px
}
</style>
